import {DlMuiDynamicIcon, serializeJSONToString, useApiQuery} from '@wallaby-ui/core';
import {
  Formatters,
  AG_GRID_COMPATIBILITY,
  HaidiEncounterDocumentLinkFormatter,
  JustTooltipFormatter,
  DEFAULT_PAGE_SIZE,
  ShortcutEnum,
} from '@wallaby-ui/document';
import {parseISO} from 'date-fns';
import {Column} from 'material-table';
import React, {useEffect, useMemo} from 'react';

import {AppConfig} from 'src/js/app_config';

export const DEFAULT_DOCUMENT_LIST_PARAMS = {
  defaultDocumentDateRangeStart: 7,
  defaultDocumentDateRangeEnd: 7,
  surgeryProtocolDateRangeStart: 30,
};
export const DEFAULT_DOC_SORTING = [
  {
    field: 'EFFECTIVE_AT',
    direction: 'desc',
  },
  {
    field: 'VERSION',
    direction: 'desc',
  },
];
export const DEFAULT_DOC_ACTIVE_SORTING = {field: 'EFFECTIVE_AT', direction: 'desc' as const};
export type EncounterDocumentsFilters = {
  documentName?: string;
  documentOrganizationIds?: string[];
  documentLastUpdatedFrom?: string;
  documentLastUpdatedTo?: string;
  documentLastUpdatedFloatingShortcutName?: ShortcutEnum | null;
};
export const DEFAULT_DOC_FILTERS: EncounterDocumentsFilters = {};
export const DEFAULT_DOC_PAGING = {};
function useSelectedDocumentPage(encounterId: string, documentVersionId: string | undefined, page: number | undefined) {
  const apiUrl = `${AppConfig.service.url.documents}${encounterId}/document-version/${documentVersionId}/page-index`;
  const apiParams: any = {
    size: DEFAULT_PAGE_SIZE,
    scope: 'EXTENDED_HOSPITALIZATION',
    ...DEFAULT_DOCUMENT_LIST_PARAMS,
  };
  const params = new URLSearchParams(apiParams);
  DEFAULT_DOC_SORTING.forEach((sortField) => {
    params.append('sort', `${sortField.field},${sortField.direction}`);
  });

  const dependOn = [apiUrl, serializeJSONToString(apiParams)];
  const {data, isLoading, isError} = useApiQuery<number>(dependOn, null, {
    axiosConfig: {
      url: apiUrl,
      params,
    },
    reactQueryOptions: {
      enabled: Boolean(documentVersionId && page === undefined),
      staleTime: 0,
      cacheTime: 0,
    },
  });

  if (page !== undefined) {
    return page;
  }
  if (!isLoading && !isError && data) {
    return data;
  }
  return undefined;
}

export function isDocumentOutsideEncounter(documentDate: Date, encounterStart: Date, encounterEnd?: Date): boolean {
  if (documentDate < encounterStart) {
    return true;
  }
  if (encounterEnd && documentDate > encounterEnd) {
    return true;
  }
  return false;
}

export function useEncounterDocumentListColumns(
  formatters: Formatters,
  defaultSort: {field: string; direction: 'asc' | 'desc'},
  encounterId: string,
  encounterStart: Date,
  encounterEnd?: Date
): Column<any>[] {
  const {t, formatDate, formatDateTime} = formatters;
  const columns: Column<any>[] = [
    {
      field: 'name',
      title: t('encounter.documentsTable.fileName'),
      tooltip: t('encounter.documentsTable.fileName.tooltip'),
      sorting: false,
      render: (data) => (
        <div style={{display: 'flex', alignItems: 'center'}}>
          <HaidiEncounterDocumentLinkFormatter {...AG_GRID_COMPATIBILITY} value={{...data, encounterId}} />
          {isDocumentOutsideEncounter(data.effectiveAt, encounterStart, encounterEnd) && (
            <DlMuiDynamicIcon
              iconTitle={t('encounter.documentsTable.fileName.outsideRangeTooltip')}
              iconName='warning'
              useFixedViewBox={12}
              svgIconStyle={{
                width: '12px',
                height: '12px',
                color: '#154FA6',
              }}
            />
          )}
        </div>
      ),
    },
    {
      field: 'organizationShortName',
      title: t('encounter.documentsTable.format'),
      tooltip: t('encounter.documentsTable.format.tooltip'),
      sorting: false,
      width: '105px',
      render: (data) => (
        <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={data?.organizationShortName} tooltipOverride={data?.organizationLongName} />
      ),
    },
    {
      field: 'EFFECTIVE_AT',
      title: t('encounter.documentsTable.createTimeStamp'),
      tooltip: t('encounter.documentsTable.createTimeStamp.tooltip'),
      sorting: true,
      width: '150px',
      customSort: (_data1, _data2, _type) => {
        return 0;
      },
      render: (data) => {
        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={formatDate(data?.effectiveAt)}
            tooltipOverride={formatDateTime(data?.effectiveAt)}
          />
        );
      },
    },
  ];
  return columns.map((v) => ({
    ...v,
    sorting: v.sorting ?? false,
    customSort: v.customSort ?? (() => 0),
    ...(v.field === defaultSort.field ? {defaultSort: defaultSort.direction} : {}),
  }));
}

type EncounterDocumentUseFiltersProps = {
  encounterId: string;
  selectedDocumentId: string | undefined;
  query: any;
  changeQuery: (newValue: any) => void;
};
export function useFilters(props: EncounterDocumentUseFiltersProps) {
  const {encounterId, selectedDocumentId, query, changeQuery} = props;
  const selectedDocumentPage = useSelectedDocumentPage(encounterId, selectedDocumentId, query?.docPaging?.page);
  const filters = useMemo(() => {
    return {
      doc: query?.doc ?? DEFAULT_DOC_FILTERS,
      docSorting: query?.docSorting ?? DEFAULT_DOC_SORTING,
      docActiveSorting: query?.docActiveSorting ?? DEFAULT_DOC_ACTIVE_SORTING,
      docPaging: {
        ...(query?.docPaging ?? DEFAULT_DOC_PAGING),
        page: query?.docPaging?.page ?? selectedDocumentPage,
      },
      docFiltersOpen: query?.docFiltersOpen ?? false,
    };
  }, [query, selectedDocumentPage]);
  useEffect(() => {
    if (!query.docPaging?.page && filters.docPaging?.page) {
      changeQuery({
        docPaging: filters.docPaging,
      });
    }
  }, [query.docPaging, filters.docPaging, changeQuery]);

  return {filters, changeFilters: changeQuery};
}
